import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface NotificationPreferences {
  email_new_followers: boolean;
  email_followed_user_runs: boolean;
  email_local_runs: boolean;
  email_run_likes: boolean;
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_new_followers: true,
    email_followed_user_runs: true,
    email_local_runs: true,
    email_run_likes: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('email_new_followers, email_followed_user_runs, email_local_runs, email_run_likes')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading notification preferences:', error);
        return;
      }

      if (data) {
        setPreferences({
          email_new_followers: data.email_new_followers ?? true,
          email_followed_user_runs: data.email_followed_user_runs ?? true,
          email_local_runs: data.email_local_runs ?? true,
          email_run_likes: data.email_run_likes ?? true,
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreference = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!user?.id) return;

    setIsUpdating(true);
    const previousValue = preferences[key];
    
    // Optimistic update
    setPreferences(prev => ({ ...prev, [key]: value }));

    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          [key]: value,
        });

      if (error) throw error;

      toast({
        title: "Notification settings updated",
        description: `${getLabelForKey(key)} ${value ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      console.error('Error updating preference:', error);
      // Rollback on error
      setPreferences(prev => ({ ...prev, [key]: previousValue }));
      toast({
        title: "Error",
        description: "Failed to update notification settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    preferences,
    isLoading,
    isUpdating,
    updatePreference,
  };
}

function getLabelForKey(key: keyof NotificationPreferences): string {
  const labels: Record<keyof NotificationPreferences, string> = {
    email_new_followers: 'New follower notifications',
    email_followed_user_runs: 'Following activity notifications',
    email_local_runs: 'Local adventure notifications',
    email_run_likes: 'Like notifications',
  };
  return labels[key];
}
